import React, { useEffect, useState } from 'react';
import './GrievanceViewTickets.css';
import { useLocation } from 'react-router-dom';
import { getData } from '../../../../Services/Services';
import { getThemeURL } from '../../../../Services/EndPoints';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

const GrievanceView = ({ data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('Open');
  const [proposedStatus, setProposedStatus] = useState(''); // Store the status user selected in dropdown
  const [actionTaken, setActionTaken] = useState('');
  const [uploadAttachments, setUploadAttachments] = useState([]);
  const [username, setUsername] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState('');
  const navigate = useNavigate();

  const location = useLocation();
  const { ticketData } = location.state || {};
  
  // Transform ticket data to match GrievanceView expected format
  const transformTicketData = (ticket) => {
    console.log("Ticket Data", ticket);
    if (!ticket) return {};

    return {
      fullName: ticket.customerName || 'Pagoti Ramakrishna',
      email: ticket.email || 'Not provided',
      phone: ticket.phoneNumber || 'Not provided',
      // alternativePhone: ticket.alternatePhoneNumber || ticket.alternativePhone || ticket.phoneNumber || 'Not provided',
      alternativePhone: ticket.alternatePhoneNumber || 'Not provided',
      relationship: ticket.relPropertyName || 'Tenant',
      propertyAddress: ticket.propertyAddress || 'Not provided',
      unitNumber: ticket.apartmentNumber || ticket.aprtmentNumber || ticket.unitNumber || 'Not provided',
      category: ticket.categoryName || ticket.category || 'Not specified',
      priority: (ticket.priority || 'low').toLowerCase(),
      subject: ticket.subject || 'No subject',
      description: ticket.description || 'No description provided',
      inprogressRemarks: ticket.inprogressRemarks || 'Not provided',
      closedRemarks: ticket.closedRemarks || 'Not provided',
      attachments: ticket.mediaList || [], // Pass directly
      originalData: ticket
    };
  };

  // Use transformed data from location state if available, otherwise use prop data
  const formattedData = ticketData ? transformTicketData(ticketData) : data || {};

  const {
    fullName,
    email,
    phone,
    alternativePhone,
    relationship,
    propertyAddress,
    unitNumber,
    category,
    priority,
    subject,
    description,
    attachments = [],
  } = formattedData;

  // Function to get user role and set restrictions
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const usr_prof = await getData(getThemeURL);
        if (usr_prof && usr_prof[0]) {
          const userData = usr_prof[0];
          setUsername(userData);
          
          // Extract role name from user data
          const roleName = userData.role_name || userData.role || '';
          setUserRole(roleName);
          
          // Log for debugging
          console.log("User role:", roleName);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    
    fetchUserData();
  }, []);

  // Update the status display based on ticketData
  useEffect(() => {
    if (ticketData?.status) {
      setSelectedStatus(ticketData.status);
    }
  }, [ticketData]);

  // Check if user is SEOC
  const isSEOC = userRole && userRole.toUpperCase() === 'SEOC';

  // Function to get record ID for API calls
  const getRecordId = () => {
    if (!ticketData) return null;
    
    // Check in originalData first
    if (ticketData.originalData?.id) {
      return ticketData.originalData.id;
    }
    else if (ticketData.originalData?.grievanceSeqId) {
      return ticketData.originalData.grievanceSeqId;
    }
    else if (ticketData.id) {
      return ticketData.id;
    }
    else if (ticketData.grievanceSeqId) {
      return ticketData.grievanceSeqId;
    }
    
    console.error("No record ID found in:", ticketData);
    return null;
  };

  // Function to get display ID (grievanceSeqId) for UI
  const getDisplayId = () => {
    if (!ticketData) return null;
    
    // Check in originalData first
    if (ticketData.originalData?.grievanceSeqId) {
      return ticketData.originalData.grievanceSeqId;
    }
    else if (ticketData.grievanceSeqId) {
      return ticketData.grievanceSeqId;
    }
    else if (ticketData.originalData?.id) {
      return ticketData.originalData.id;
    }
    else if (ticketData.id) {
      return ticketData.id;
    }
    
    return null;
  };

  // Function to get ticket ID - try multiple possible fields
  const getTicketId = () => {
    return getDisplayId();
  };

  // Function to get user ID
  const getUserId = () => {
    let userId = null;
    
    // Try to get from the username state
    if (username && username.id) {
      userId = username.id;
    } 
    // If not in state, try to get from localStorage
    else {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          userId = parsedUser.username?.id || parsedUser.id || parsedUser.userId || parsedUser.user_id;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    }
    
    return userId;
  };

  // API call function
  const updateGrievanceStatus = async (endPoint, grievanceData, files = []) => {
    try {
      const formData = new FormData();

      // Append grievance JSON
      formData.append('grievance', JSON.stringify(grievanceData));

      // ✅ FIX: Always send files key
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      } else {
        formData.append(
          'files',
          new Blob([], { type: 'application/octet-stream' })
        );
      }

      const fullEndPoint = `/grievance/${endPoint}`;
      let url = '/' + process.env.REACT_APP_CORE_CONTEXT_PATH_NAME + fullEndPoint;

      const response = await axios.post(url, formData, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('auth_token'),
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Error updating grievance to ${endPoint}:`, error);
      throw error;
    }
  };

  const formatLabel = (label) => label || 'Not provided';

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    const currentStatus = ticketData?.status || 'Open';
    
    // If user is selecting the same status, no need for modal
    if (newStatus === currentStatus) {
      setSelectedStatus(newStatus);
      return;
    }
    
    // Store the proposed new status and open modal
    setProposedStatus(newStatus);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    // When modal is closed without submitting, revert to current status
    setIsModalOpen(false);
    setActionTaken('');
    setUploadAttachments([]);
    setProposedStatus(''); // Clear proposed status
  };

  const handleActionChange = (event) => {
    setActionTaken(event.target.value);
  };

  const handleSubmit = async () => {
    // Get record ID (id field from API)
    const recordId = getRecordId();
    
    if (!recordId) {
      alert('Record ID not found. Please check console for details.');
      console.error('Record ID not found in:', ticketData);
      return;
    }

    // Get user ID
    const userId = getUserId();
    
    if (!userId) {
      alert('User ID not found. Please make sure you are logged in.');
      return;
    }

    // Validate action taken for non-Open status
    if (proposedStatus !== 'Open' && !actionTaken.trim()) {
      alert(`Please provide action taken for ${proposedStatus} status`);
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare grievance data object
      const grievanceData = {
        id: recordId,
        updated_by: userId
      };
      
      // Add remarks based on status
      if (proposedStatus === 'In Progress' && actionTaken) {
        grievanceData.inprogressRemarks = actionTaken;
      } else if (proposedStatus === 'Closed' && actionTaken) {
        grievanceData.closedRemarks = actionTaken;
      }
      
      console.log("Grievance data to send:", grievanceData);
      
      // Determine endpoint based on status
      let endpoint = '';
      if (proposedStatus === 'In Progress') {
        endpoint = 'inprogress';
      } else if (proposedStatus === 'Closed') {
        endpoint = 'closed';
      }
      
      // Call the API
      const response = await updateGrievanceStatus(endpoint, grievanceData, uploadAttachments);
      
      if (response) {
        toast.success('Ticket status updated successfully!', {
          position: 'top-center'
        });
        
        // Update local state with the proposed status
        setSelectedStatus(proposedStatus);
        if (ticketData) {
          if (ticketData.originalData) {
            ticketData.originalData.status = proposedStatus;
          }
          ticketData.status = proposedStatus;
        }
        
        // Close modal and reset
        closeModal();
        navigate("../grievance-rental-tickets");
      } else {
        throw new Error('No response from server');
      }
      
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert(`Failed to update ticket: ${error.message || 'Please try again'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to get available status options based on current status and user role
  const getStatusOptions = () => {
    const currentStatus = selectedStatus; // Use selectedStatus instead of ticketData?.status
    const options = [];
    
    // Only SEOC users can change status
    if (!isSEOC) {
      // For non-SEOC users, only show current status
      options.push({ value: currentStatus, label: currentStatus });
      return options;
    }
    
    // For SEOC users, show available transitions
    switch(currentStatus) {
      case 'Open':
        // Can change to In Progress or Closed
        options.push(
          { value: 'Open', label: 'Open' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Closed', label: 'Closed' }
        );
        break;
      case 'In Progress':
        // Can only change to Closed, not back to Open
        options.push(
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Closed', label: 'Closed' }
        );
        break;
      case 'Closed':
        // Once closed, cannot change status
        options.push({ value: 'Closed', label: 'Closed' });
        break;
      default:
        options.push({ value: currentStatus, label: currentStatus });
    }
    
    return options;
  };

  // Function to get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Open': return 'gv-status-badge-open';
      case 'In Progress': return 'gv-status-badge-inprogress';
      case 'Closed': return 'gv-status-badge-closed';
      default: return 'gv-status-badge-open';
    }
  };

  const statusOptions = getStatusOptions();
  const displayTicketId = getTicketId();
  // eslint-disable-next-line no-unused-vars
  const currentStatus = selectedStatus; // Use selectedStatus for display

  return (
    <div className="gv-container">
      <div className="gv-header">
        <div>
          <h1 className="gv-title">Grievance Details</h1>
          {displayTicketId && (
            <p className="gv-ticket-id">Ticket ID: {displayTicketId}</p>
          )}
        </div>
        <div className="gv-status-container">
          <span className="gv-status-label">Ticket Status:</span>
          
          {/* Show dropdown for SEOC users, status badge for others */}
          {isSEOC ? (
            <select 
              value={selectedStatus} 
              onChange={handleStatusChange} 
              className="gv-status-dropdown"
              disabled={isSubmitting || selectedStatus === 'Closed'}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <span className={`gv-status-badge ${getStatusColor(selectedStatus)}`}>
              {selectedStatus}
            </span>
          )}
        </div>
      </div>

      <div className="gv-grid">
        {/* User Details */}
        <section className="gv-card">
          <div className="gv-card-header">
            <span className="gv-card-icon gv-user-icon" />
            <h2>User Details</h2>
          </div>
          <div className="gv-card-body">
            <div className="gv-field-row">
              <div className="gv-field">
                <span className="gv-field-label">Full Name</span>
                <span className="gv-field-value">{formatLabel(fullName)}</span>
              </div>
              <div className="gv-field">
                <span className="gv-field-label">Email ID</span>
                <span className="gv-field-value gv-link">{formatLabel(email)}</span>
              </div>
            </div>

            <div className="gv-field-row">
              <div className="gv-field">
                <span className="gv-field-label">Phone Number</span>
                <span className="gv-field-value">{formatLabel(phone)}</span>
              </div>
              <div className="gv-field">
                <span className="gv-field-label">Alternative Number</span>
                <span className="gv-field-value">
                  {alternativePhone ? alternativePhone : 'Not provided'}
                </span>
              </div>
            </div>

            <div className="gv-field-row">
              <div className="gv-field">
                <span className="gv-field-label">Relationship to Property</span>
                <span className="gv-field-value gv-tag">
                  {relationship || 'Not specified'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Property Details */}
        <section className="gv-card">
          <div className="gv-card-header">
            <span className="gv-card-icon gv-home-icon" />
            <h2>Property Details</h2>
          </div>
          <div className="gv-card-body">
            <div className="row mb-2">
              <div className="col-6">
                <span className="gv-field-label">Property Address</span>
              </div>
              <div className="col-6">
                <p className="gv-field-value gv-multiline mb-0">
                  {propertyAddress || 'Not provided'}
                </p>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-6">
                <span className="gv-field-label">Unit/Apartment Number</span>
              </div>
              <div className="col-6">
                <span className="gv-field-value">
                  {unitNumber || 'Not provided'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Issue Details */}
        <section className="gv-card gv-issue-card">
          <div className="gv-card-header">
            <span className="gv-card-icon gv-issue-icon" />
            <h2>Issue Details</h2>
            <div className="gv-chip-row">
              <span className={`gv-chip gv-chip-priority-${priority || 'low'}`}>
                Priority: {priority ? priority.toUpperCase() : 'LOW'}
              </span>
              <span className="gv-chip gv-chip-category">
                Category: {category || 'Category not set'}
              </span>
            </div>
          </div>

          <div className="gv-card-body">
            <div className="gv-field">
              <span className="gv-field-label">Title</span>
              <span className="gv-field-value">{subject || 'Not provided'}</span>
            </div>

            <div className="gv-field">
              <span className="gv-field-label">Description</span>
              <p className="gv-field-value gv-multiline">
                {description || 'No description provided'}
              </p>
            </div>

            {(selectedStatus === 'In Progress' && formattedData.inprogressRemarks ) && (
              <div className="gv-field">
              <span className="gv-field-label">Status Action Message (In Progress)</span>
              <p className="gv-field-value gv-multiline">
                {formattedData.inprogressRemarks }
              </p>
            </div>
            )}

            {(selectedStatus === 'Closed' && formattedData.closedRemarks ) && (
              <div className="gv-field">
              <span className="gv-field-label">Status Action Message (Closed)</span>
              <p className="gv-field-value gv-multiline">
                {formattedData.closedRemarks }
              </p>
            </div>
            )}

            <div className="gv-field">
              <span className="gv-field-label">Attachments</span>
              {attachments.length === 0 ? (
                <span className="gv-field-value">No attachments</span>
              ) : (
                <div className="gv-attachments-grid">
                  {attachments.map((media, index) => {
                    const isImage = media.mediaPath
                      ? /\.(png|jpe?g|gif|webp)$/i.test(media.mediaPath)
                      : false;

                    return (
                      <div className="gv-attachment-card" key={index}>
                        <div className="gv-attachment-thumb">
                          {isImage ? (
                            <img
                              src={media.mediaPath}
                              alt={media.fileName || `attachment-${index}`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = ''; // Placeholder image
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <div className="gv-file-icon">
                              <span className="gv-file-ext">
                                {(media.fileName || 'FILE')
                                  .split('.')
                                  .pop()
                                  .toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="gv-attachment-meta">
                          <span className="gv-attachment-name" title={media.fileName}>
                            {media.fileName || `Attachment ${index + 1}`}
                          </span>
                        </div>
                        {media.mediaPath && (
                          <a
                            href={media.mediaPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="gv-attachment-link"
                          >
                            View
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Modal (only for SEOC users) */}
        {isModalOpen && isSEOC && (
          <div className="gv-modal">
            <div className="gv-modal-content">
              <h2 className='modal-header'>Update Ticket Status</h2>
              <p>Changing status from <strong>{selectedStatus}</strong> to <strong>{proposedStatus}</strong></p>
              {displayTicketId && <p><strong>Ticket ID:</strong> {displayTicketId}</p>}
              
              <div className="gv-modal-field">
                <label>Action Taken / Remarks *</label>
                <textarea
                  value={actionTaken}
                  onChange={handleActionChange}
                  placeholder="Enter action taken or remarks..."
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="gv-modal-field">
                <label>Add Attachments (Optional)</label>
                <div className="file-input-wrapper">
                  <label className="file-input-label">
                    <span>Click to upload files</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const validFiles = files.filter(file => {
                          const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
                          const maxSize = 10 * 1024 * 1024;
                          if (!validTypes.includes(file.type)) {
                            alert(`File ${file.name} has invalid type. Only PNG, JPG, JPEG, PDF allowed.`);
                            return false;
                          }
                          if (file.size > maxSize) {
                            alert(`File ${file.name} is too large. Max size is 10MB.`);
                            return false;
                          }
                          return true;
                        });
                        setUploadAttachments(prev => [...prev, ...validFiles]);
                      }}
                      style={{ display: 'none' }}
                      accept=".png,.jpg,.jpeg,.pdf"
                      disabled={isSubmitting}
                    />
                  </label>
                </div>
                {uploadAttachments.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#4a5568' }}>
                    {uploadAttachments.length} file(s) selected
                  </div>
                )}
                {uploadAttachments.length > 0 && (
                  <div className="file-preview-wrapper" style={{ marginTop: '1rem' }}>
                    <h4>Selected Files:</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {uploadAttachments.map((file, index) => (
                        <div key={index} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          {file.type.startsWith('image/') ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`attachment-${index}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                              <span style={{ fontSize: '10px', wordBreak: 'break-all' }}>{file.name.substring(0, 15)}...</span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const newAttachments = uploadAttachments.filter((_, i) => i !== index);
                              setUploadAttachments(newAttachments);
                            }}
                            style={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              background: 'red',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="gv-modal-buttons">
                <button 
                  onClick={closeModal} 
                  className="gv-btn gv-btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSubmit} 
                  className="gv-btn gv-btn-submit"
                  disabled={isSubmitting || (proposedStatus !== 'Open' && !actionTaken.trim())}
                >
                  {isSubmitting ? 'Updating...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrievanceView;